import { useEffect, useRef } from 'react';

/* ─── Data ─────────────────────────────────────────────────── */
const enableItems = [
  {
    title: 'Faster Sales Velocity',
    text: 'Product-market fit from day one ensures demand-backed launches that sell faster with fewer discounts.',
  },
  {
    title: 'Better Product-Market Fit',
    text: 'Every unit type, size & price point is backed by data before a single design line is drawn.',
  },
  {
    title: 'Reduced Development Risk',
    text: 'Architecture-led feasibility eliminates costly redesigns and reduces approval friction significantly.',
  },
  {
    title: 'Land Project Positioning',
    text: 'A tailored narrative attracts the right developers and investor profiles, maximising land value.',
  },
  {
    title: 'Structured Investment Opportunities',
    text: 'Capital meets real-world risk-adjusted, deadline-driven opportunities with full transparency.',
  },
];

const quoteStats = [
  { value: '500', suffix: '+', label: 'Projects Facilitated' },
  { value: '50',  suffix: '+', label: 'Acres Optimized' },
  { value: '200', suffix: '+', label: 'Investors Active' },
  { value: '1K',  suffix: '+', label: 'Units Delivered' },
];

/* ─── Minimal SectionHeader shim (replace with your real one) ─ */
function SectionHeader({ label, title, subtitle }) {
  return (
    <div className="es-header">
      <span className="es-label">{label}</span>
      <h2 className="es-title">{title}</h2>
      <p className="es-subtitle">{subtitle}</p>
    </div>
  );
}

/* ─── Hook: intersection-based reveal ─────────────────────────── */
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('es-revealed');
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return ref;
}

/* ─── Component ───────────────────────────────────────────────── */
export default function EnablesSection() {
  const sectionRef = useReveal(0.1);

  return (
    <>
      <style>{CSS}</style>
      <section id="enables" data-section ref={sectionRef} className="es-section">
        {/* Architectural grid backdrop */}
        {/* <div className="es-grid-bg" aria-hidden="true">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="es-grid-line" style={{ '--i': i }} />
          ))}
        </div> */}

        {/* Gold accent bar */}
        <div className="es-accent-bar" aria-hidden="true" />

        <div className="es-wrap">
          <SectionHeader
            label="The Outcome"
            title="What This Enables"
            subtitle="Our model creates compounding advantages across the entire value chain."
          />

          <div className="es-body">
            {/* ── Left: numbered list ─────────────────────── */}
            <div className="es-list">
              {enableItems.map((item, index) => (
                <div
                  key={item.title}
                  className="es-item"
                  style={{ '--delay': `${index * 90}ms` }}
                >
                  <div className="es-num-col">
                    <span className="es-num">0{index + 1}</span>
                    {index < enableItems.length - 1 && (
                      <span className="es-num-line" />
                    )}
                  </div>
                  <div className="es-item-content">
                    <h3 className="es-item-title">{item.title}</h3>
                    <p className="es-item-text">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* ── Right: quote + stats card ────────────────── */}
            <div className="es-card">
              {/* Top ornament */}
              <div className="es-card-ornament" aria-hidden="true">
                <span /><span /><span />
              </div>

              {/* <div className="es-engine-graphic" aria-hidden="true">
                <svg viewBox="0 0 360 300" role="presentation">
                  <defs>
                    <linearGradient id="esEngineLine" x1="0" x2="1" y1="0" y2="1">
                      <stop offset="0%" stopColor="#00d4c8" stopOpacity="0.85" />
                      <stop offset="58%" stopColor="#58a6ff" stopOpacity="0.38" />
                      <stop offset="100%" stopColor="#d8b45f" stopOpacity="0.62" />
                    </linearGradient>
                  </defs>
                  <path className="es-engine-route es-engine-route-one" d="M180 38 L318 236 L42 236 Z" />
                  <path className="es-engine-route es-engine-route-two" d="M180 90 L260 210 L100 210 Z" />
                  <path className="es-engine-axis" d="M180 38 V236 M42 236 H318 M100 210 H260" />
                </svg>

                <div className="es-engine-core">
                  <span>TRIKON</span>
                  <strong>Outcome Engine</strong>
                </div>
                <span className="es-engine-node es-node-land">Land</span>
                <span className="es-engine-node es-node-design">Design</span>
                <span className="es-engine-node es-node-capital">Capital</span>
                
              </div> */}

              <blockquote className="es-quote">
                <span className="es-quote-mark">&ldquo;</span>
                Better decisions lead to better projects.
                <span className="es-quote-mark es-quote-mark--close">&rdquo;</span>
              </blockquote>

              <p className="es-quote-copy">
                More informed design, stronger demand intelligence, and smarter capital
                alignment — these three decisions made better lead to dramatically
                better outcomes, every time.
              </p>

              {/* Divider */}
              <div className="es-divider" aria-hidden="true" />

              {/* Stats grid */}
              <div className="es-stats">
                {quoteStats.map((stat, i) => (
                  <div
                    key={stat.label}
                    className="es-stat"
                    style={{ '--delay': `${200 + i * 80}ms` }}
                  >
                    <div className="es-stat-value">
                      {stat.value}
                      <span className="es-stat-suffix">
                        {stat.suffix}
                      </span>
                    </div>
                    <div className="es-stat-label">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Bottom corner ornament */}
              <div className="es-card-corner" aria-hidden="true" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

/* ─── Styles ──────────────────────────────────────────────────── */
const CSS = `
/* ── TOKENS ─────────────────────────────────────────────── */
.es-section {
  --es-bg: #050f1c;
  --es-surface: rgba(6, 22, 42, 0.78);

  --es-border: rgba(0, 212, 200, 0.12);
  --es-border-mid: rgba(0, 212, 200, 0.22);

  --es-gold: #00d4c8;
  --es-gold-dim: rgba(0, 212, 200, 0.55);

  --es-text-1: #e8f0f8;
  --es-text-2: #7a9ab8;
  --es-text-3: #3a5a78;

  --es-serif: 'Inter', sans-serif;
  --es-sans: 'Inter', sans-serif;
}

/* ── BASE ────────────────────────────────────────────────── */
.es-section {
  position: relative;
  background:
    radial-gradient(circle at 15% 20%, rgba(0,212,200,.08) 0%, transparent 30%),
    radial-gradient(circle at 85% 10%, rgba(0,120,210,.08) 0%, transparent 30%),
    linear-gradient(180deg, #050f1c 0%, #04111f 100%);
  padding: 120px 0 140px;
  overflow: hidden;
  font-family: var(--es-sans);
}

/* ── GRID BACKGROUND ─────────────────────────────────────── */
.es-grid-bg {
  position: absolute;
  inset: 0;
  display: flex;
  pointer-events: none;
}

.es-grid-line {
  flex: 1;
  border-right: 1px solid var(--es-border);
  opacity: 0;
  transform: scaleY(0);
  transform-origin: top;
  transition:
    opacity 1.2s ease calc(var(--i) * 120ms),
    transform 1.4s cubic-bezier(0.16,1,0.3,1) calc(var(--i) * 120ms);
}

.es-revealed .es-grid-line {
  opacity: 1;
  transform: scaleY(1);
}

/* ── TOP ACCENT BAR ─────────────────────────────────────── */
.es-accent-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    var(--es-gold) 30%,
    var(--es-gold) 70%,
    transparent 100%
  );
  transform: scaleX(0);
  transition: transform 1.4s cubic-bezier(0.16,1,0.3,1) 0.2s;
}

.es-revealed .es-accent-bar {
  transform: scaleX(1);
}

/* ── WRAP ───────────────────────────────────────────────── */
.es-wrap {
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 40px;
}

/* ── HEADER ─────────────────────────────────────────────── */
.es-header {
  margin-bottom: 72px;
}

.es-label {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: var(--es-gold);
  margin-bottom: 20px;
  opacity: 0;
  transform: translateY(12px);
  transition: opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s;
}

.es-revealed .es-label {
  opacity: 1;
  transform: translateY(0);
}

.es-title {
  // font-size: clamp(42px, 5vw, 64px);
  font-size: 46px;
  font-weight: 800;
  color: var(--es-text-1);
  letter-spacing: -0.03em;
  line-height: 1.1;
  margin: 0 0 20px;
  opacity: 0;
  transform: translateY(16px);
  transition: opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s;
}

.es-revealed .es-title {
  opacity: 1;
  transform: translateY(0);
}

.es-subtitle {
  font-size: 15px;
  font-weight: 400;
  color: var(--es-text-2);
  letter-spacing: 0.01em;
  line-height: 1.8;
  max-width: 500px;
  margin: 0;
  opacity: 0;
  transform: translateY(12px);
  transition: opacity 0.7s ease 0.3s, transform 0.7s ease 0.3s;
}

.es-revealed .es-subtitle {
  opacity: 1;
  transform: translateY(0);
}

/* ── BODY ───────────────────────────────────────────────── */
.es-body {
  display: grid;
  grid-template-columns: 1fr 420px;
  gap: 80px;
  align-items: start;
}

@media (max-width: 1024px) {
  .es-body {
    grid-template-columns: 1fr;
    gap: 64px;
  }
}

/* ── LIST ───────────────────────────────────────────────── */
.es-list {
  display: flex;
  flex-direction: column;
}

.es-item {
  display: grid;
  grid-template-columns: 64px 1fr;
  gap: 0 24px;
  opacity: 0;
  transform: translateX(-20px);
  transition:
    opacity 0.6s ease calc(var(--delay, 0ms) + 300ms),
    transform 0.6s cubic-bezier(0.16,1,0.3,1) calc(var(--delay, 0ms) + 300ms);
}

.es-revealed .es-item {
  opacity: 1;
  transform: translateX(0);
}

.es-num-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 2px;
}

.es-num {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--es-gold);
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: rgba(0,212,200,0.08);
  border: 1px solid rgba(0,212,200,0.14);
  display: flex;
  align-items: center;
  justify-content: center;
}

.es-num-line {
  display: block;
  width: 1px;
  flex: 1;
  min-height: 32px;
  background: linear-gradient(to bottom, var(--es-gold-dim), transparent);
  margin: 10px 0;
  opacity: 0.5;
}

.es-item-content {
  padding-bottom: 40px;
}

.es-item:last-child .es-item-content {
  padding-bottom: 0;
}

.es-item-title {
  font-size: 17px;
  font-weight: 700;
  color: var(--es-text-1);
  letter-spacing: -0.02em;
  margin: 0 0 10px;
  line-height: 1.3;
}

.es-item-text {
  font-size: 14px;
  font-weight: 400;
  color: var(--es-text-2);
  line-height: 1.8;
  margin: 0;
}

/* ── CARD ───────────────────────────────────────────────── */
.es-card {
  position: relative;
  background: var(--es-surface);
  border: 1px solid var(--es-border);
  padding: 48px 44px 44px;
  overflow: hidden;

  backdrop-filter: blur(18px);

  box-shadow:
    0 20px 40px rgba(0, 0, 0, 0.22),
    inset 0 1px 0 rgba(255,255,255,0.02);

  opacity: 0;
  transform: translateY(24px);

  transition:
    opacity 0.8s ease 0.4s,
    transform 0.8s cubic-bezier(0.16,1,0.3,1) 0.4s,
    border-color 0.35s ease,
    box-shadow 0.35s ease;
}

.es-card:hover {
  border-color: rgba(0, 212, 200, 0.32);

  box-shadow:
    0 24px 50px rgba(0, 212, 200, 0.12),
    0 20px 40px rgba(0,0,0,0.25);
}

.es-revealed .es-card {
  opacity: 1;
  transform: translateY(0);
}

.es-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;

  // background: linear-gradient(
  //   90deg,
  //   transparent,
  //   var(--es-gold),
  //   transparent
  // );
}

/* ── ORNAMENT ───────────────────────────────────────────── */
.es-card-ornament {
  display: flex;
  gap: 6px;
  margin-bottom: 36px;
}

.es-card-ornament span {
  display: block;
  height: 2px;
  background: var(--es-gold);
}

.es-card-ornament span:nth-child(1) {
  width: 32px;
}

.es-card-ornament span:nth-child(2) {
  width: 12px;
  opacity: 0.5;
}

.es-card-ornament span:nth-child(3) {
  width: 6px;
  opacity: 0.25;
}

/* ── QUOTE ──────────────────────────────────────────────── */
.es-quote-mark {
  font-size: 48px;
  line-height: 0;
  vertical-align: -12px;
  color: var(--es-gold);
  display: inline;
  font-weight: 700;
}

.es-quote-mark--close {
  margin-left: 2px;
}

.es-quote {
  font-size: clamp(22px, 2.2vw, 30px);
  font-weight: 700;
  color: var(--es-text-1);
  line-height: 1.4;
  margin: 0 0 24px;
  letter-spacing: -0.03em;
}

.es-quote-copy {
  font-size: 16px;
  color: var(--es-text-2);
  line-height: 1.9;
  margin: 0;
}

/* ── DIVIDER ────────────────────────────────────────────── */
.es-divider {
  margin: 36px 0 32px;
  height: 1px;

  background: linear-gradient(
    90deg,
    rgba(0,212,200,0.5),
    transparent
  );

  opacity: 0.5;
}

/* ── STATS ──────────────────────────────────────────────── */
.es-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.es-stat {
  opacity: 0;
  transform: translateY(14px);

  transition:
    opacity 0.5s ease calc(var(--delay, 0ms) + 500ms),
    transform 0.5s cubic-bezier(0.16,1,0.3,1)
      calc(var(--delay, 0ms) + 500ms);
}

.es-revealed .es-stat {
  opacity: 1;
  transform: translateY(0);
}

.es-stat-value {
  font-size: clamp(32px, 3vw, 42px);
  font-weight: 800;
  color: var(--es-text-1);
  line-height: 1;
  margin-bottom: 6px;
  letter-spacing: -0.03em;
}

.es-stat-suffix {
  color: var(--es-gold);
  font-size: 0.65em;
  vertical-align: super;
  margin-left: 1px;
}

.es-stat-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--es-text-3);
}

/* ── CORNER ─────────────────────────────────────────────── */
.es-card-corner {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 60px;
  height: 60px;

  // border-top: 1px solid var(--es-border-mid);
  // border-left: 1px solid var(--es-border-mid);

  opacity: 0.6;
}

/* ── RESPONSIVE ─────────────────────────────────────────── */
@media (max-width: 768px) {
  .es-section {
    padding: 80px 0 100px;
  }

  .es-wrap {
    padding: 0 24px;
  }

  .es-header {
    margin-bottom: 52px;
  }

  .es-card {
    padding: 36px 28px 32px;
  }

  .es-stats {
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }
}

@media (max-width: 480px) {
  .es-wrap {
    padding: 0 20px;
  }

  .es-item {
    grid-template-columns: 48px 1fr;
  }

  .es-stats {
    grid-template-columns: 1fr;
  }

  .es-title {
    font-size: 38px;
  }

  .es-quote {
    font-size: 24px;
  }
}

.es-section {
  isolation: isolate;
  background:
    linear-gradient(115deg, transparent 0 36%, rgba(0, 212, 200, 0.055) 36.2%, transparent 36.8%),
    linear-gradient(292deg, transparent 0 62%, rgba(88, 166, 255, 0.052) 62.2%, transparent 62.8%),
    radial-gradient(circle at 18% 18%, rgba(0, 212, 200, 0.12), transparent 26%),
    radial-gradient(circle at 88% 14%, rgba(216, 180, 95, 0.08), transparent 24%),
    linear-gradient(180deg, #020b14 0%, #061423 48%, #020b14 100%);
}

.es-section::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -2;
  background-image:
    linear-gradient(rgba(0, 212, 200, 0.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 212, 200, 0.045) 1px, transparent 1px);
  background-size: 92px 92px;
  mask-image: linear-gradient(to bottom, transparent, black 14%, black 86%, transparent);
  opacity: 0.66;
}

.es-section::after {
  content: '';
  position: absolute;
  inset: auto -12% 8% auto;
  z-index: -1;
  width: 520px;
  height: 520px;
  border: 1px solid rgba(0, 212, 200, 0.09);
  transform: rotate(45deg);
}

.es-wrap {
  max-width: 1200px;
}

.es-header {
  max-width: 760px;
  margin-bottom: 62px;
}

.es-label {
  position: relative;
  padding-left: 56px;
}

.es-label::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 38px;
  height: 1px;
  background: linear-gradient(90deg, var(--es-gold), transparent);
}

.es-title {
  // font-size: clamp(38px, 4.6vw, 66px);
  font-size: 46px;
  letter-spacing: 0;
}

.es-subtitle {
  max-width: 610px;
}

.es-body {
  grid-template-columns: minmax(0, 1.05fr) minmax(360px, 0.95fr);
  gap: 34px;
  align-items: start;
}

.es-list {
  position: relative;
  gap: 14px;
  padding-left: 26px;
}

.es-list::before {
  content: '';
  position: absolute;
  left: 15px;
  top: 20px;
  bottom: 20px;
  width: 1px;
  background: linear-gradient(to bottom, transparent, rgba(0, 212, 200, 0.58), rgba(216, 180, 95, 0.28), transparent);
  box-shadow: 0 0 26px rgba(0, 212, 200, 0.24);
}

.es-item {
  grid-template-columns: 52px 1fr;
  gap: 0 18px;
  min-height: 118px;
  padding: 20px 22px 20px 0;
  border: 1px solid rgba(232, 240, 248, 0.07);
  border-left-color: transparent;
  border-radius: 8px;
  background:
    linear-gradient(90deg, rgba(7, 24, 43, 0.94), rgba(4, 15, 29, 0.64)),
    rgba(5, 17, 31, 0.82);
  overflow: hidden;
}

.es-item::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    linear-gradient(135deg, rgba(0, 212, 200, 0.13), transparent 36%),
    linear-gradient(315deg, rgba(216, 180, 95, 0.08), transparent 42%);
  opacity: 0;
  transition: opacity 0.35s ease;
}

.es-item:hover {
  border-color: rgba(0, 212, 200, 0.28);
  box-shadow: 0 24px 54px rgba(0, 0, 0, 0.2);
  transform: translateX(8px);
}

.es-item:hover::before {
  opacity: 1;
}

.es-num-col,
.es-item-content {
  position: relative;
  z-index: 1;
}

.es-num {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #020b14;
  border-color: rgba(0, 212, 200, 0.52);
  box-shadow:
    0 0 0 6px rgba(0, 212, 200, 0.06),
    0 0 22px rgba(0, 212, 200, 0.22);
}

.es-num-line {
  display: none;
}

.es-item-content {
  padding-bottom: 0;
}

.es-item-title {
  font-size: 20px;
  letter-spacing: 0;
  margin-bottom: 8px;
}

.es-item-text {
  font-size: 16px;
  line-height: 1.7;
  max-width: 570px;
}

.es-card {
  min-height: 100%;
  padding: 34px;
  border-radius: 8px;
  border-color: rgba(0, 212, 200, 0.22);
  background:
    linear-gradient(180deg, rgba(7, 24, 43, 0.9), rgba(3, 13, 25, 0.94)),
    radial-gradient(circle at 50% 30%, rgba(0, 212, 200, 0.12), transparent 35%);
}

.es-card::before {
  height: 100%;
//   background:
//     linear-gradient(90deg, rgba(0, 212, 200, 0.18) 1px, transparent 1px),
//     linear-gradient(rgba(0, 212, 200, 0.14) 1px, transparent 1px);
//   background-size: 70px 70px;
//   opacity: 0.28;
// }

.es-card::after {
  content: '';
  position: absolute;
  top: 88px;
  left: 50%;
  width: 250px;
  height: 220px;
  transform: translateX(-50%);
  border: 1px solid rgba(0, 212, 200, 0.34);
  clip-path: polygon(50% 0, 100% 100%, 0 100%);
  filter: drop-shadow(0 0 26px rgba(0, 212, 200, 0.16));
  opacity: 0.72;
}

.es-card-ornament,
.es-quote,
.es-quote-copy,
.es-divider,
.es-stats {
  position: relative;
  z-index: 1;
}

.es-card-ornament {
  justify-content: flex-end;
  margin-bottom: 150px;
}

.es-quote {
  max-width: 390px;
  font-size: clamp(25px, 2.5vw, 34px);
  letter-spacing: 0;
  margin-top: 20px;
}

.es-quote-copy {
  color: var(--es-text-2);
}

.es-divider {
  margin: 30px 0 24px;
}

.es-stats {
  gap: 12px;
}

.es-stat {
  padding: 18px;
  border: 1px solid rgba(232, 240, 248, 0.08);
  border-radius: 8px;
  background: rgba(2, 11, 20, 0.48);
}

.es-stat-value {
  font-size: clamp(28px, 2.6vw, 38px);
}

.es-stat-label {
  color: rgba(232, 240, 248, 0.52);
}

.es-card-corner {
  width: 90px;
  height: 90px;
  border-color: rgba(216, 180, 95, 0.24);
}

@media (max-width: 1024px) {
  .es-body {
    grid-template-columns: 1fr;
  }

  .es-card {
    min-height: auto;
  }
}

@media (max-width: 768px) {
  .es-body {
    gap: 24px;
  }

  .es-list {
    padding-left: 16px;
  }

  .es-list::before {
    left: 8px;
  }

  .es-item {
    grid-template-columns: 44px 1fr;
    padding: 18px 18px 18px 0;
  }

  .es-card-ornament {
    margin-bottom: 120px;
  }

  .es-card::after {
    top: 78px;
    width: 210px;
    height: 185px;
  }
}

@media (max-width: 480px) {
  .es-item-title {
    font-size: 18px;
  }

  .es-card {
    padding: 28px 22px;
  }
}

.es-section {
  min-height: 100vh;
  padding: 54px 0 58px;
}

.es-header {
  margin-bottom: 24px;
}

.es-title {
  // font-size: clamp(34px, 4vw, 54px);
  font-size: 46px;
  margin-bottom: 12px;
}

.es-subtitle {
  font-size: 14px;
  line-height: 1.65;
}

.es-body {
  grid-template-columns: minmax(390px, 0.96fr) minmax(0, 1.04fr);
  gap: 28px;
}

.es-card {
  order: 2;
  min-height: 200px;
  padding: 24px 28px 24px;
}

.es-list {
  order: 1;
  gap: 10px;
}

.es-item {
  min-height: 86px;
  padding-top: 12px;
  padding-bottom: 12px;
}

.es-item-title {
  font-size: 20px;
  margin-bottom: 5px;
}

.es-item-text {
  font-size: 16px;
  line-height: 1.2;
}

.es-card-ornament {
  margin-bottom: 0;
}

.es-engine-graphic {
  position: relative;
  z-index: 1;
  height: 230px;
  margin: 4px 0 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.es-engine-graphic svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
}

.es-engine-route {
  fill: none;
  stroke: url(#esEngineLine);
  stroke-width: 1.4;
  stroke-linejoin: round;
  stroke-dasharray: 720;
  stroke-dashoffset: 720;
  filter: drop-shadow(0 0 14px rgba(0, 212, 200, 0.18));
  transition: stroke-dashoffset 1.8s cubic-bezier(0.16, 1, 0.3, 1) 0.35s;
}

.es-engine-route-two {
  opacity: 0.55;
  transition-delay: 0.55s;
}

.es-engine-axis {
  fill: none;
  stroke: rgba(232, 240, 248, 0.11);
  stroke-width: 1;
  stroke-dasharray: 7 9;
}

.es-revealed .es-engine-route {
  stroke-dashoffset: 0;
}

.es-engine-core {
  position: relative;
  z-index: 2;
  width: 124px;
  height: 124px;
  border: 1px solid rgba(0, 212, 200, 0.32);
  border-radius: 50%;
  background:
    linear-gradient(180deg, rgba(0, 212, 200, 0.16), rgba(4, 15, 29, 0.96)),
    rgba(4, 15, 29, 0.92);
  box-shadow:
    0 0 44px rgba(0, 212, 200, 0.12),
    inset 0 0 34px rgba(0, 212, 200, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.es-engine-core span {
  color: var(--es-text-1);
  font-size: 22px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: 0.06em;
}

.es-engine-core strong {
  display: block;
  margin-top: 10px;
  color: var(--es-text-2);
  font-size: 10px;
  line-height: 1.4;
  font-weight: 400;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.es-engine-node {
  position: absolute;
  z-index: 3;
  padding: 8px 13px;
  border: 1px solid rgba(0, 212, 200, 0.22);
  border-radius: 999px;
  background: rgba(2, 11, 20, 0.9);
  color: var(--es-text-1);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.22);
  opacity: 0;
  transform: translateY(10px);
  transition: opacity 0.55s ease, transform 0.55s ease;
}

.es-revealed .es-engine-node {
  opacity: 1;
  transform: translateY(0);
}

.es-node-land {
  left: 10%;
  bottom: 9%;
  transition-delay: 0.8s;
}

.es-node-design {
  top: 2%;
  left: 50%;
  transform: translate(-50%, 10px);
  transition-delay: 0.68s;
}

.es-revealed .es-node-design {
  transform: translate(-50%, 0);
}

.es-node-capital {
  right: 5%;
  bottom: 9%;
  transition-delay: 0.86s;
}

.es-node-demand {
  right: 11%;
  top: 45%;
  border-color: rgba(216, 180, 95, 0.32);
  color: #f0d992;
  transition-delay: 0.94s;
}

.es-card::after {
  display: none;
}

.es-quote {
  max-width: none;
  font-size: 21px;
  line-height: 1.2;
  margin-bottom: 12px;
  margin-top: 50px;
}

.es-quote-mark {
  font-size: 34px;
  vertical-align: -8px;
}

.es-quote-copy {
  font-size: 16px;
  line-height: 1.2;
  font-weight: 200;
}

.es-divider {
  margin: 18px 0 14px;
}

.es-stats {
  gap: 8px;
}

.es-stat {
  padding: 12px;
}

.es-stat-value {
  font-size: 36px;
}

.es-stat-label {
  font-size: 11px;
  letter-spacing: 0.11em;
}

@media (max-width: 1100px) {
  .es-section {
    min-height: auto;
    padding: 76px 0 82px;
  }

  .es-body {
    grid-template-columns: 1fr;
  }

  .es-card,
  .es-list {
    order: initial;
  }
}

@media (max-width: 640px) {
  .es-engine-graphic {
    height: 248px;
  }

  .es-engine-core {
    width: 122px;
    height: 122px;
  }

  .es-engine-core span {
    font-size: 21px;
  }

  .es-engine-node {
    font-size: 10px;
    padding: 7px 10px;
  }

  .es-node-demand {
    right: 4%;
  }
}
`;


